import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../../utils/api";
import {
  FiImage,
  FiThumbsUp,
  FiMessageCircle,
  FiEye,
} from "react-icons/fi";
import ShareButton from "../../components/ShareButton";
import PostCommentsModal from "../../components/posts/PostCommentsModal";
import SubscribeButton from "../../components/SubscribeButton";
import "./post.css";

const GUEST_USER_ID = "000000000000000000000000";

export default function Post() {

  const navigate = useNavigate();

  /* ================= USER ================= */

  // ? Important: undefined initially (prevents race condition)
  const [user, setUser] = useState(undefined);
  const userId = user?._id || null;

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        setUser(null);
      }
    } else {
      setUser(null);
    }
  }, []);

  /* ================= MODAL STATE ================= */

  const [activePost, setActivePost] = useState(null);

  /* ================= CREATE POST ================= */

  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");

  /* ================= FEED STATE ================= */

  const [posts, setPosts] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastCreatedAt, setLastCreatedAt] = useState(null);

  const viewedRef = useRef(new Set());
  const observer = useRef();

  /* ================= LOAD FEED ================= */

  const loadFeed = async (reset = false) => {
    if (loadingFeed) return;

    try {
      setLoadingFeed(true);

      const res = await api.get("/client/posts/feed", {
        params: {
          limit: 10,
          lastCreatedAt: reset ? null : lastCreatedAt,
          ...(userId ? { userId } : {})
        },
      });

      const newPosts = res.data?.status ? res.data.data || [] : [];

      if (reset) {
        setPosts(newPosts);
        setHasMore(true);
      } else {
        setPosts((prev) => [...prev, ...newPosts]);
      }

      if (newPosts.length < 10) setHasMore(false);

      if (newPosts.length > 0) {
        setLastCreatedAt(newPosts[newPosts.length - 1].createdAt);
      }
    } catch {
      setHasMore(false);
    } finally {
      setLoadingFeed(false);
    }
  };

  // ? Wait until user is resolved before loading feed
  useEffect(() => {
    if (user === undefined) return;
    loadFeed(true);
  }, [user]);

  /* ================= INFINITE SCROLL ================= */

  const lastPostRef = useCallback(
    (node) => {
      if (loadingFeed) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadFeed();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loadingFeed, hasMore]
  );

  /* ================= CREATE POST ================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId) {
      window.location.href = "/login";
      return;
    }

    if (!file && !caption.trim()) {
      return setMessage("Write something or add an image");
    }

    const fd = new FormData();
    if (file) fd.append("file", file);
    fd.append("caption", caption);
    fd.append("privacy", "public");
    fd.append("userId", userId);

    try {
      setUploading(true);
      setMessage("");

      const res = await api.post("/client/posts/upload", fd);

      if (res.data?.status) {
        setCaption("");
        setFile(null);
        setLastCreatedAt(null);
        loadFeed(true);
        setMessage("Post uploaded successfully");
      } else {
        setMessage("Upload failed");
      }
    } catch {
      setMessage("Server error");
    } finally {
      setUploading(false);
    }
  };

  /* ================= NAVIGATE TO CHANNEL ================= */

  const goToChannel = (channelId) => {
    if (!channelId) return;

    if (userId) {
      navigate(`/user/channel/${channelId}`);
    } else {
      navigate(`/channel/${channelId}`);
    }
  };

  /* ================= LIKE ================= */

  const toggleLike = async (id) => {
    if (!userId) {
      window.location.href = "/login";
      return;
    }

    try {
      const res = await api.post(`/client/posts/${id}/like`, { userId });

      if (res.data?.status) {
        setPosts((prev) =>
          prev.map((p) =>
            p._id === id
              ? { ...p, likesCount: res.data.likesCount }
              : p
          )
        );
      }
    } catch {}
  };

  /* ================= VIEW TRACK ================= */

  useEffect(() => {
    if (!posts.length) return;

    const unviewedPosts = posts.filter(
      (p) => !viewedRef.current.has(p._id)
    );

    if (!unviewedPosts.length) return;

    unviewedPosts.forEach((post) => {
      viewedRef.current.add(post._id);

      api.post(`/client/posts/${post._id}/view`, {
        userId: userId || GUEST_USER_ID,
      }).catch(() => {});
    });

    setPosts((prev) =>
      prev.map((p) =>
        unviewedPosts.some((u) => u._id === p._id)
          ? { ...p, viewsCount: (p.viewsCount || 0) + 1 }
          : p
      )
    );
  }, [posts.length, userId]);

  /* ================= COMMENT COUNT UPDATE ================= */

  const handleCommentAdded = (postId) => {
    setPosts((prev) =>
      prev.map((p) =>
        p._id === postId
          ? { ...p, commentsCount: (p.commentsCount || 0) + 1 }
          : p
      )
    );
  };

 

  /* ================= RENDER ================= */

  return (
    <>
    <Helmet>
      <title>Posts - ShareVideo</title>
      <meta
        name="description"
        content="Browse and share posts on ShareVideo. Discover creative photo posts, updates, and engaging content from creators."
      />
      <link rel="canonical" href="https://sharevideo.in/post" />
    </Helmet>

    <div className="post-page">

      {/* CREATE POST */}
      <div className="create-bar">

        {user && (
          <img
            src={user.image || user.avatar || "/default-avatar.png"}
            alt="avatar"
            className="create-avatar"
          />
        )}

        <textarea
          placeholder="What's on your mind?"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
        />

        <label className="image-btn">
          <FiImage />
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={(e) => setFile(e.target.files[0])}
          />
        </label>

        <button onClick={handleSubmit} disabled={uploading}>
          {uploading ? "Posting..." : "Post"}
        </button>
      </div>

      {message && <div className="msg">{message}</div>}

      {/* POSTS */}
      {posts.map((post, index) => {
        const isLast = index === posts.length - 1;

        return (
          <div
            key={post._id}
            className="post-card"
            ref={isLast ? lastPostRef : null}
          >

            {/* ? AUTHOR + SUBSCRIBE ROW */}
            <div className="author-row">

              <div
                className="author"
                onClick={() => goToChannel(post.channelId)}
                style={{ cursor: "pointer" }}
              >
                <img
                  src={post.userAvatar || "/default-avatar.png"}
                  alt="avatar"
                  className="author-avatar"
                />
                <strong>{post.userName || "User"}</strong>
              </div>

              {post.channelId && (
                <SubscribeButton
                  channelId={post.channelId}
                  initialSubscribed={post.isSubscribed}
                />
              )}

            </div>

            {post.caption && (
              <div className="post-text">{post.caption}</div>
            )}

            {(post.mediaUrl || post.media?.url) && (
              <div className="post-media">
                <img
                  src={post.mediaUrl || post.media?.url}
                  alt="post"
                  className="post-image"
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = "/placeholder.png";
                  }}
                />
              </div>
            )}

            <div className="actions">
              <span onClick={() => toggleLike(post._id)}>
                <FiThumbsUp />
                {post.likesCount || 0}
              </span>

              <span onClick={() => setActivePost(post)}>
                <FiMessageCircle />
                {post.commentsCount || 0}
              </span>

              <span>
                <FiEye />
                {post.viewsCount || 0}
              </span>

              <ShareButton
                type="post"
                id={post._id}
                shareCount={post.sharesCount || 0}
              />
            </div>

          </div>
        );
      })}

      {loadingFeed && <p className="loading">Loading...</p>}
      {!hasMore && <p className="end">No more posts</p>}

      {activePost && (
        <PostCommentsModal
          post={activePost}
          currentUser={user}
          onClose={() => setActivePost(null)}
          onCommentAdded={() =>
            handleCommentAdded(activePost._id)
          }
        />
      )}
    </div>
   </>
  );
}